import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconBell, IconBrandTeams, IconMail } from '@tabler/icons-react';

import {
  createOrrScoreDroppedSubscriptions,
  deleteAlertSubscription,
  fetchMyAlertSubscriptions,
  fetchNotificationOptions,
  updateAlertSubscriptionChannels,
  type NotificationOption,
} from '@/domains/alerts';
import useToast from '@/shared/stores/toastStore';

/**
 * WK blue used for the "active / subscribed" affordance across the variants.
 */
export const ALERT_BLUE = '#005b92';
export const ALERT_BLUE_BG = '#e6f2f9';

/**
 * Channel glyphs. Tabler is already the icon family of this action bar
 * (IconSettings/IconPlayerPlay) and ships a real Microsoft Teams brand glyph,
 * so it renders reliably where the WK `chat` glyph was coming up blank.
 */
export const ChannelIcon = ({
  channelKey,
  size = 16,
  className,
}: {
  channelKey: string;
  size?: number;
  className?: string;
}) => {
  switch (channelKey.toLowerCase()) {
    case 'email':
      return <IconMail size={size} className={className} />;
    case 'teams':
      return <IconBrandTeams size={size} className={className} />;
    default:
      return <IconBell size={size} className={className} />;
  }
};

export interface UseScoreDropAlertsArgs {
  bitId: string;
  applicationName: string;
  environment: string;
}

interface MutationVars {
  next: string[];
  enabling: boolean;
  /** Set when a single channel was toggled (for per-row feedback + toast copy). */
  channelKey?: string;
  channelLabel?: string;
}

/**
 * Shared data + mutation layer for the per-application "score dropped" alert
 * subscription. The BIT Id and environment are known from the page, so a single
 * action covers the whole lifecycle:
 *   - no subscription yet, enabling a channel -> create,
 *   - last channel disabled                   -> delete,
 *   - otherwise                               -> update channels.
 *
 * Backs every subscribe/unsubscribe variant so they stay behaviourally
 * identical and reflect the same live subscription.
 */
export function useScoreDropAlerts({
  bitId,
  applicationName,
  environment,
}: UseScoreDropAlertsArgs) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: optionsData } = useQuery({
    queryKey: ['notification-options'],
    queryFn: async () => (await fetchNotificationOptions()).data,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const channels = useMemo<NotificationOption[]>(
    () => optionsData?.channels?.filter((c) => c.enabled) ?? [],
    [optionsData],
  );

  const { data: subscriptions, isLoading: subsLoading } = useQuery({
    queryKey: ['alert-subscriptions', 'mine'],
    queryFn: async () => (await fetchMyAlertSubscriptions()).data,
  });

  // The single score-dropped subscription matching this app + environment.
  const subscription = useMemo(
    () =>
      (subscriptions ?? []).find(
        (s) =>
          (s.details?.application_id ?? '').toLowerCase() ===
            bitId.toLowerCase() &&
          (s.details?.environment ?? '').toLowerCase() ===
            environment.toLowerCase(),
      ),
    [subscriptions, bitId, environment],
  );
  const activeChannels = useMemo(
    () => subscription?.alertChannels ?? [],
    [subscription],
  );
  const isSubscribed = activeChannels.length > 0;

  const mutation = useMutation({
    mutationFn: async ({ next }: MutationVars) => {
      if (!subscription) {
        return createOrrScoreDroppedSubscriptions([
          {
            applicationId: bitId,
            applicationName,
            environment,
            alertChannels: next,
          },
        ]);
      }
      if (next.length === 0) {
        return deleteAlertSubscription(subscription.id);
      }
      return updateAlertSubscriptionChannels(subscription.id, next);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['alert-subscriptions', 'mine'],
      });
      const scope = variables.channelLabel
        ? `${variables.channelLabel} alerts`
        : 'Score-drop alerts';
      showToast('success', {
        title: variables.enabling ? 'Subscribed' : 'Unsubscribed',
        message: `${scope} ${
          variables.enabling ? 'enabled' : 'disabled'
        } for ${applicationName} in ${environment}.`,
      });
    },
    onError: () => {
      showToast('error', {
        title: 'Could not update subscription',
        message: 'An error occurred updating your alert subscription.',
      });
    },
  });

  const apply = (next: string[], meta: Omit<MutationVars, 'next'>) => {
    // Nothing to create when there is no subscription and nothing to enable.
    if (!subscription && next.length === 0) return;
    mutation.mutate({ next, ...meta });
  };

  /** Toggle a single channel on/off (used by the per-channel variants). */
  const toggleChannel = (key: string, label: string) => {
    const enabling = !activeChannels.includes(key);
    const next = enabling
      ? [...activeChannels, key]
      : activeChannels.filter((c) => c !== key);
    apply(next, { enabling, channelKey: key, channelLabel: label });
  };

  /** Subscribe to every channel at once, or unsubscribe entirely. */
  const toggleAll = () => {
    const enabling = !isSubscribed;
    apply(enabling ? channels.map((c) => c.key) : [], { enabling });
  };

  const labelFor = (key: string) =>
    channels.find((c) => c.key === key)?.label ?? key;

  return {
    channels,
    activeChannels,
    isSubscribed,
    busy: mutation.isPending || subsLoading,
    /** Channel currently being saved, for per-row spinners (null for toggleAll). */
    pendingChannel: mutation.isPending
      ? (mutation.variables?.channelKey ?? null)
      : null,
    ready: channels.length > 0,
    toggleChannel,
    toggleAll,
    labelFor,
    applicationName,
    environment,
  };
}
